<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FilterFunnelIcon from '@/components/FilterFunnelIcon.vue';
import { navigation, type NavigationItem } from './navigation';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';
import { useReplenishmentStore } from '@/features/replenishment/replenishment.store';
import { preloadApplication, preloadRouteComponents } from './preload';
import { networkPendingRequests } from '@/services/http';
import { useVersionGuard } from '@/composables/useVersionGuard';
import { useDevMode } from '@/devmode/useDevMode';
import ScopeNotice from '@/components/ScopeNotice.vue';

const app = useAppStore();
/** 系统更新检测（只有生产会真正启用，本地开发不打扰）。
 *  解构出模板要用的两个 ref：嵌套在对象里的 ref 在模板中不会自动解包。 */
const versionGuard = useVersionGuard();
const {
  updateReady: updateTipVisible,
  updateLabel: updateTipLabel,
  reloadNow: reloadForUpdate,
  dismissUpdate: dismissUpdateTip,
} = versionGuard;
const auth = useAuthStore();
const replenishment = useReplenishmentStore();
/** 开发模式（devmode）：开关状态、是否本地环境都由零框架核心模块推送过来，
 *  本组件只负责渲染按钮 + 调用 toggle，不另发请求、不与 useVersionGuard 抢状态。 */
const dm = useDevMode();
const route = useRoute();
/**
 * 1.1.85: 数据范围受限横幅只在「核心决策页」顶部出现。
 * 仪表盘 / 工厂可出货页此前**已有**各自的提示（且不走这个组件），为避免重复不再列入；
 * 下面这些才是评估里发现"静默删行、用户不知自己少看了数据"的重灾区：
 * 备货计划 / 在库 / 在途 / 库龄 / 销量 / 销量分析 / 工厂订单 / 工厂可出货 / 仓点总览。
 * 横幅本身会判 null（不受限）→ 不渲染，所以普通管理员在这里看不到任何提示。
 */
const SCOPE_NOTICE_PATHS = new Set<string>([
  '/replenishment',
  '/inventory',
  '/inventory/transit',
  '/inventory/age',
  '/sales',
  '/sales/analysis',
  '/inventory/factory-orders',
  '/inventory/factory-shippable',
  '/warehouse-overview',
]);
const scopeNoticeVisible = computed(() => SCOPE_NOTICE_PATHS.has(route.path));
const router = useRouter();
const hoverMenu = ref<string | null>(null);
const subMenuPos = reactive({ left: 0, top: 0 });
/**
 * 侧边栏自定义瞬时 tooltip：原生 title 有 ~1s 延迟，收起态下图标没有文字，
 * 必须"鼠标一放上去就出来"，所以自己画一个浮层（无延迟，跟随元素右侧垂直居中）。
 */
const tip = reactive({ visible: false, text: '', top: 0, left: 0 });
function showTip(text: string, event: MouseEvent): void {
  const rect = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();
  if (!rect) return;
  tip.text = text;
  // 垂直居中于触发元素；贴顶/贴底时留出边距，避免浮层被视口裁掉
  tip.top = Math.max(22, Math.min(window.innerHeight - 22, Math.round(rect.top + rect.height / 2)));
  tip.left = Math.round(rect.right + 8);
  tip.visible = true;
}
function hideTip(): void {
  tip.visible = false;
}
/** 侧边栏是否按"展开样式"渲染（收起 = 56px 图标条，标签隐藏） */
const navExpanded = computed(() => !app.sidebarCollapsed);
/** 侧边栏宽度：收起 = 原来的 56px 图标条（不回退成"整个藏起来"） */
const asideStyle = computed<Record<string, string>>(() => ({
  width: app.sidebarCollapsed ? '56px' : '208px',
  transition: 'width 200ms ease',
}));

// 收起/展开切换后，浮层依附的元素已变，直接关掉，避免残留在错误位置
watch(
  () => app.sidebarCollapsed,
  () => hideTip(),
);
let hoverTimer: number | undefined;
let replenishmentWarmupDelay: number | undefined;
let replenishmentWarmupTimer: number | undefined;
let applicationWarmupDelay: number | undefined;
let networkLoadingDelay: number | undefined;
let networkLoadingMinimum: number | undefined;
const showNetworkLoading = ref(false);
let networkLoadingShownAt = 0;
let disposed = false;

watch(
  networkPendingRequests,
  (count) => {
    window.clearTimeout(networkLoadingDelay);
    window.clearTimeout(networkLoadingMinimum);
    // 1.1.118: 页面自己出了「居中加载层」时静音这颗药丸 ✓（否则顶上 + 中间两个圈 ✗）
    if (app.networkPillMuted) {
      showNetworkLoading.value = false;
      return;
    }
    if (count > 0) {
      if (showNetworkLoading.value) return;
      networkLoadingDelay = window.setTimeout(() => {
        networkLoadingShownAt = Date.now();
        showNetworkLoading.value = true;
      }, 120);
      return;
    }
    if (!showNetworkLoading.value) return;
    networkLoadingMinimum = window.setTimeout(
      () => {
        showNetworkLoading.value = false;
      },
      Math.max(0, 280 - (Date.now() - networkLoadingShownAt)),
    );
  },
  { immediate: true },
);

async function warmReplenishment(): Promise<void> {
  if (
    route.path === '/replenishment' ||
    !auth.isAuthenticated ||
    !auth.can('function.replenishment.view') ||
    networkPendingRequests.value > 0
  )
    return;
  if (!replenishment.activities.length || !replenishment.skus.length) {
    await Promise.all([replenishment.loadActivities(), replenishment.loadSkus()]);
  }
  await replenishment.load(false, { silent: true });
}

async function waitForForegroundIdle(): Promise<boolean> {
  while (!disposed) {
    if (networkPendingRequests.value === 0) {
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      if (networkPendingRequests.value === 0) return true;
    } else {
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
  }
  return false;
}

async function warmApplication(): Promise<void> {
  if (!(await waitForForegroundIdle())) return;
  await preloadApplication(auth.can);
  if (!(await waitForForegroundIdle())) return;
  await preloadRouteComponents(auth.can);
  if (disposed) return;
  replenishmentWarmupDelay = window.setTimeout(() => void warmReplenishment(), 1_500);
}

const visibleNavigation = computed(() =>
  navigation
    .map((item) => ({
      ...item,
      children: item.children?.filter((child) => !child.permission || auth.can(child.permission)),
    }))
    .filter((item) => (!item.permission || auth.can(item.permission)) && (!item.children || item.children.length > 0)),
);
watchEffect(() => {
  if (route.meta.permission && !auth.can(String(route.meta.permission))) {
    const first = visibleNavigation.value
      .flatMap((item) => (item.to ? [item] : item.children || []))
      .find((item) => !item.permission || auth.can(item.permission));
    void router.replace(first?.to || '/dashboard');
  }
});
const activeGroup = computed(
  () => visibleNavigation.value.find((item) => item.children?.some((child) => child.to === route.path))?.key,
);
function enterMenu(item: NavigationItem, event?: MouseEvent): void {
  window.clearTimeout(hoverTimer);
  // 移到「没有子菜单」的项（仪表盘/备货分析/销量数据…）时立刻收起上一个展开的子菜单：
  // 否则只清了收起定时器、hoverMenu 仍停在上一项，子菜单会一直挂在那里不收（实测反馈的 bug）。
  if (!item.children?.length) {
    hoverMenu.value = null;
    return;
  }
  hoverMenu.value = item.key;
  const element = event?.currentTarget as HTMLElement | null;
  if (element) {
    const bounds = element.getBoundingClientRect();
    subMenuPos.left = bounds.right;
    subMenuPos.top = bounds.top;
  }
}
function keepMenu(): void {
  window.clearTimeout(hoverTimer);
}
function leaveMenu(): void {
  hoverTimer = window.setTimeout(() => (hoverMenu.value = null), 180);
}
function open(item: NavigationItem): void {
  hoverMenu.value = null;
  if (item.to) void router.push(item.to);
}
function roleLabel(role?: string): string {
  return (
    (
      {
        owner: '超级超级管理员',
        super_admin: '超级管理员',
        admin: '管理员',
        custom: '自定义角色',
        editor: '编辑',
        viewer: '只读',
      } as Record<string, string>
    )[role || ''] ||
    role ||
    ''
  );
}
/**
 * 1.1.78: 全局筛选的选单 —— 自绘浮层（不用原生 select：它在深色侧边栏里弹出的是系统白框，观感突兀、
 * 也没法做动画与"每个模板多少个 SPU"这类信息）。
 * 控件放在**侧边栏自己那一列**（不是浮在页面上）：既不盖住页面内容、也不用页面让出内边距。
 * 选完走 app.setFilterTemplate(id)：写 localStorage → 亮过渡遮罩 → 整页刷新 → 弹成功提示。
 */
const filterMenuOpen = ref(false);
/**
 * 1.1.79: 鼠标悬停在筛选行上时，右侧浮出一张预览卡 —— 主要用来显示**完整的范围名**
 * （侧边栏只有 208px，行内名字会被截断成「演示小…」），顺带说明这条范围的作用。
 * 选单展开时不显示，避免两张浮层叠在一起。
 */
const filterPreviewOpen = ref(false);

function pickFilterTemplate(id: number | null): void {
  filterMenuOpen.value = false;
  if (id === app.activeFilterTemplateId) return; // 点的就是当前项，不折腾
  app.setFilterTemplate(id);
}

/** 收起态那个小图标：先展开侧边栏，再把选单打开（一下点到想要的东西） */
function openFilterPickerFromCollapsed(): void {
  if (app.sidebarCollapsed) app.toggleSidebar();
  window.setTimeout(() => {
    filterMenuOpen.value = true;
  }, 60);
}
/* ===== 1.1.120: 本地开发「后端旧进程」自检 =====
   用户反复反馈"改了后端代码、页面数字不变"✗ —— 根因几乎都是本地后端进程没重启（前端热更新正常）。
   这里每隔 20s 问一次 /api/version：后端会对比「源码最新修改时间」与「进程启动时间」，
   源码更新 → stale=true → 顶部亮红条，直接告诉你该重启，不用再猜 ✓。
   只有 env=local 才轮询 ✓（生产不扫盘、不打扰）。 */
let backendPoller: number | undefined;
function fmtClock(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
const backendStartedLabel = computed(() => fmtClock(app.backendStartedAt));
const backendSourceLabel = computed(() => fmtClock(app.backendSourceAt));
async function probeBackendFreshness(): Promise<void> {
  await app.loadVersion();
  if (app.backendEnv === 'local') {
    if (!backendPoller) backendPoller = window.setInterval(() => void app.loadVersion(), 20_000);
    return;
  }
  if (backendPoller) {
    window.clearInterval(backendPoller);
    backendPoller = undefined;
  }
}

onMounted(() => {
  disposed = false;
  void probeBackendFreshness();
  // 本地测试通过门禁状态（生产 env=prod，按钮自动隐藏）
  if (auth.isAuthenticated) void app.checkTestPass();
  // 全局筛选的可选模板（权限管理里配置；没有配置时筛选控件不显示）
  if (auth.isAuthenticated) void app.loadFilterTemplates();
  // 刚切换过筛选（整页刷新）→ 弹一次成功提示，明确告知当前范围
  const filterToast = app.consumeFilterToast();
  if (filterToast) app.notify(filterToast, 'success');
  // 1.1.66 起有系统更新检测；1.1.73 起：一检测到新版就在**页面正上方**出可点击的升级横幅，
  //   空闲时横幅展示 20s 后仍静默刷新（详见 useVersionGuard.ts）
  void versionGuard.start();
  // Let the first route start and finish its foreground request before shared
  // stores compete for bandwidth. The idle check below still starts sooner
  // once that request has settled.
  applicationWarmupDelay = window.setTimeout(() => void warmApplication(), 1_000);
  replenishmentWarmupTimer = window.setInterval(() => void warmReplenishment(), 5 * 60 * 1_000);
});
onBeforeUnmount(() => {
  disposed = true;
  if (backendPoller) {
    window.clearInterval(backendPoller);
    backendPoller = undefined;
  }
  versionGuard.stop();
  window.clearTimeout(hoverTimer);
  window.clearTimeout(replenishmentWarmupDelay);
  window.clearTimeout(applicationWarmupDelay);
  window.clearInterval(replenishmentWarmupTimer);
  window.clearTimeout(networkLoadingDelay);
  window.clearTimeout(networkLoadingMinimum);
});
</script>

<template>
  <div class="relative flex h-screen overflow-hidden">
    <aside
      class="sidebar-nav relative bg-slate-900 text-slate-200 flex flex-col flex-shrink-0"
      :class="navExpanded ? '' : 'sidebar-collapsed'"
      :style="asideStyle"
    >
      <!-- 1.1.69: 展开态左右内边距 20px → 16px —— 收起把手挪进标题行后占掉 32px，
           「备货系统 + 版本号」实测需要 99px 而原来只剩 95px（差 4px 会被 truncate 掉版本号） -->
      <div class="border-b border-slate-700/50" :class="navExpanded ? 'px-4 py-4' : 'px-2 py-4'">
        <div v-if="!navExpanded" class="flex justify-center">
          <button
            class="group w-10 h-10 rounded-xl flex items-center justify-center text-xl bg-gradient-to-br from-sky-500/25 to-indigo-500/25 ring-1 ring-sky-400/20 shadow-lg shadow-sky-950/40 hover:from-sky-500/40 hover:to-indigo-500/45 hover:ring-sky-300/40 hover:scale-105 active:scale-95 transition"
            aria-label="展开侧边栏"
            @click="app.toggleSidebar()"
            @mouseenter="showTip('展开侧边栏', $event)"
            @mouseleave="hideTip"
          >
            <span class="relative flex items-center justify-center">
              <span class="transition-all duration-200 group-hover:scale-75 group-hover:opacity-0">📦</span>
              <svg
                class="absolute inset-0 m-auto text-sky-300 scale-75 opacity-0 transition-all duration-200 group-hover:scale-100 group-hover:opacity-100"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9.5 5.5 16 12l-6.5 6.5" />
              </svg>
            </span>
          </button>
        </div>
        <div v-else class="flex items-center gap-2">
          <span class="text-2xl">📦</span>
          <div class="flex-1 min-w-0">
            <div class="text-base font-semibold text-white truncate">
              备货系统 <span class="text-[10px] font-normal text-slate-400 align-middle">{{ app.versionLabel }}</span>
            </div>
            <div class="text-[10px] text-slate-400 truncate">库存与销量管理</div>
          </div>
          <!-- 1.1.69: 「收起侧边栏」把手挪进侧边栏**内部**（原来贴在右边缘外，用户反馈"凸出来"）。
               放标题行右端：不越界、不遮挡菜单；提示改用项目自带的浮层 .side-tip
               （原生 title 有 ~1s 延迟且样式是浏览器默认，用户反馈"不好看"）。 -->
          <button
            class="sidebar-toggle"
            aria-label="收起侧边栏"
            @click="app.toggleSidebar()"
            @mouseenter="showTip('收起侧边栏', $event)"
            @mouseleave="hideTip"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M14.5 5.5 8 12l6.5 6.5" />
            </svg>
          </button>
        </div>
      </div>
      <!-- 1.1.79: 整条导航 select-none —— 点菜单/点快了会"概率性"把文字选上，纯操作控件不该可选 -->
      <nav class="flex-1 select-none py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
        <div
          v-for="item in visibleNavigation"
          :key="item.key"
          @mouseenter="enterMenu(item, $event)"
          @mouseleave="leaveMenu"
        >
          <a
            href="#"
            :class="[
              'group flex items-center py-1.5 text-sm transition cursor-pointer',
              !navExpanded ? 'justify-center gap-0 px-0 mx-1.5 rounded-xl hover:bg-slate-800/70' : 'gap-3 px-5',
              route.path === item.to || activeGroup === item.key ? 'nav-active' : '',
              hoverMenu === item.key ? 'bg-slate-800' : '',
            ]"
            @click.prevent="open(item)"
            @mouseenter="!navExpanded && !item.children ? showTip(item.label, $event) : undefined"
            @mouseleave="hideTip"
          >
            <span
              class="nav-icon text-lg text-center transition-all duration-150 group-hover:scale-110"
              :class="
                !navExpanded
                  ? 'w-9 h-9 rounded-lg flex items-center justify-center group-hover:bg-slate-700/60'
                  : 'w-5'
              "
              >{{ item.icon }}</span
            ><template v-if="navExpanded"
              ><span>{{ item.label }}</span>
              <!-- 子菜单箭头：原来是实心 ▶（用户反馈"生硬"），改成圆角线性 chevron，
                   子菜单悬停展开时旋转 90° 指下，过渡更柔 -->
              <svg
                v-if="item.children"
                class="nav-chevron"
                :class="hoverMenu === item.key ? 'is-open' : ''"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.4"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" />
              </svg></template
            >
          </a>
        </div>
        <div
          v-if="hoverMenu && visibleNavigation.find((item) => item.key === hoverMenu)?.children"
          class="fixed min-w-[170px] bg-slate-800 border border-slate-700 rounded-lg shadow-2xl py-1 z-50"
          :style="{ left: `${subMenuPos.left}px`, top: `${subMenuPos.top}px` }"
          @mouseenter="keepMenu"
          @mouseleave="leaveMenu"
        >
          <a
            v-for="child in visibleNavigation.find((item) => item.key === hoverMenu)?.children"
            :key="child.key"
            href="#"
            :class="[
              'flex items-center gap-2 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 hover:text-white transition cursor-pointer',
              route.path === child.to ? 'bg-slate-700 text-white font-medium' : '',
            ]"
            @click.prevent="open(child)"
          >
            <span class="w-1 h-1 rounded-full bg-slate-500"></span><span>{{ child.label }}</span>
          </a>
        </div>
      </nav>
      <div
        class="border-t border-slate-700/50"
        :class="!navExpanded ? 'px-1 py-3 flex flex-col items-center gap-2' : 'px-3 py-3'"
      >
        <!-- 1.1.78: 全局筛选 —— 放在侧边栏**自己这一列**里（不是浮在页面上）：
             不遮挡任何页面内容、也不需要页面让出内边距，两边互不影响。
             选中的模板会随每个请求发给后端，与本人数据范围取交集后再过滤（只窄不宽）。 -->
        <template v-if="navExpanded">
          <!-- 1.1.78（二次返工）：不再套"卡片里的卡片" —— 直接做成**一行导航项**，
               与上面的菜单行同款（px-5 / py-2 / text-sm / 18px 图标 / hover 底色），
               当前范围靠右显示，生效时加一行 10px 的浅色说明。字号与层级都跟导航一致，不再另起一套语言。 -->
          <div v-if="app.filterTemplates.length" class="relative">
            <!-- 1.1.79: 全部收在**同一行**里 —— 漏斗 + 范围名 + 下拉箭头 + 清除小叉，
                 不再有下面那行"生效说明"（原来两行占地方）。行内结构：
                 左边是切换按钮（占满剩余宽度），最右边是 14px 的小叉。 -->
            <!-- select-none：这一行/浮层都是操作控件，点快了会"概率性地"把文字选上（用户反馈） -->
            <div
              class="flex w-full select-none items-center gap-2 px-5 py-2 text-sm transition"
              :class="app.activeFilterTemplate ? 'bg-slate-800/60 text-white' : 'text-slate-200 hover:bg-slate-800/70'"
              @mouseenter="filterPreviewOpen = true"
              @mouseleave="filterPreviewOpen = false"
            >
              <button
                class="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                :aria-expanded="filterMenuOpen"
                aria-haspopup="listbox"
                :aria-label="
                  app.activeFilterTemplate ? `当前筛选范围「${app.activeFilterTemplate.name}」，点击切换` : '选择筛选范围'
                "
                @click="filterMenuOpen = !filterMenuOpen"
              >
                <!-- 1.1.79: 实心漏斗（原来是描边空心，在深色栏里显得细弱） -->
                <FilterFunnelIcon
                  class="h-[18px] w-[18px] shrink-0 transition-colors"
                  :class="app.activeFilterTemplate ? 'text-sky-400' : 'text-slate-400'"
                />
                <!-- 208px 宽下"标签 + 值"会互相挤（实测标签被压成 0 宽）→
                     有筛选时直接拿范围名当主文案，没筛选才显示"筛选范围 + 全部"。 -->
                <span class="min-w-0 flex-1 truncate" :class="app.activeFilterTemplate ? 'text-sky-200' : ''">{{
                  app.activeFilterTemplate?.name || '筛选范围'
                }}</span>
                <span v-if="!app.activeFilterTemplate" class="shrink-0 text-[11px] text-slate-500">全部</span>
                <svg
                  class="h-3 w-3 shrink-0 text-slate-500 transition-transform duration-200"
                  :class="filterMenuOpen ? '-rotate-90' : ''"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              <!-- 清除：14px 小叉，紧挨在右侧；没有筛选时不渲染（不占位） -->
              <button
                v-if="app.activeFilterTemplate"
                class="shrink-0 rounded p-0.5 text-slate-500 transition hover:bg-slate-700 hover:text-white"
                aria-label="清除筛选"
                title="清除筛选，恢复「全部数据」"
                @click="pickFilterTemplate(null)"
              >
                <svg
                  class="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>

            <!-- 1.1.79: 悬停预览 —— 行内只有 208px，名字必然被截断（如「演示小…」）。
                 这里在**正上方**给完整名字：左端与行内名字对齐（left-12 = px-5 + 18px 图标 + 10px gap），
                 origin-bottom-left 让它正是从"那个缩写的字"处放大延伸出来（见 .filter-preview-* 动画）。
                 pointer-events-none：纯展示不挡点击；选单展开时不显示。 -->
            <Transition name="filter-preview">
              <div
                v-if="filterPreviewOpen && !filterMenuOpen"
                class="pointer-events-none absolute bottom-full left-12 z-[138] mb-1.5 max-w-[15rem] origin-bottom-left rounded-lg bg-slate-800/95 px-2.5 py-1.5 text-[12px] font-medium leading-snug text-white shadow-2xl ring-1 ring-slate-700 backdrop-blur"
              >
                {{ app.activeFilterTemplate?.name || '全部数据' }}
              </div>
            </Transition>

            <!-- 选项浮层：从触发器**右侧**弹出（侧边栏只有 208px，往右展才有舒服的宽度；
                 对齐到触发器底部、向上生长，避免贴到视口下沿被裁）。 -->
            <Transition name="filter-menu">
              <div
                v-if="filterMenuOpen"
                class="absolute bottom-0 left-full z-[140] ml-2 w-64 select-none overflow-hidden rounded-xl bg-slate-800 p-1.5 shadow-2xl ring-1 ring-slate-700"
                role="listbox"
              >
                <!-- 左侧小箭头，指向侧边栏里的触发器 -->
                <span
                  class="absolute -left-1 bottom-3 h-2 w-2 rotate-45 bg-slate-800 ring-1 ring-slate-700"
                  aria-hidden="true"
                ></span>
                <button
                  class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] transition duration-150"
                  :class="
                    !app.activeFilterTemplate
                      ? 'bg-slate-700/70 text-white'
                      : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                  "
                  role="option"
                  :aria-selected="!app.activeFilterTemplate"
                  @click="pickFilterTemplate(null)"
                >
                  <span class="w-3 shrink-0 text-sky-400">{{ !app.activeFilterTemplate ? '✓' : '' }}</span>
                  <span>全部数据</span>
                </button>
                <button
                  v-for="item in app.filterTemplates"
                  :key="item.id"
                  class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[11px] transition duration-150"
                  :class="
                    app.activeFilterTemplateId === item.id
                      ? 'bg-slate-700/70 text-white'
                      : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                  "
                  role="option"
                  :aria-selected="app.activeFilterTemplateId === item.id"
                  @click="pickFilterTemplate(item.id)"
                >
                  <span class="w-3 shrink-0 text-sky-400">{{ app.activeFilterTemplateId === item.id ? '✓' : '' }}</span>
                  <span class="min-w-0 flex-1 truncate">{{ item.name }}</span>
                  <span class="shrink-0 text-[10px] text-slate-500">{{ item.spu_count }} SPU</span>
                </button>
                <p class="mt-1 border-t border-slate-700/60 px-2 pt-1.5 text-[10px] text-slate-500">
                  在「权限管理 → 筛选范围设置」里维护
                </p>
              </div>
            </Transition>

            <!-- 点空白收起选单（透明层，不可见；层号低于浮层、高于页面） -->
            <div v-if="filterMenuOpen" class="fixed inset-0 z-[139]" @click="filterMenuOpen = false"></div>
          </div>
        </template>
        <template v-else-if="app.filterTemplates.length">
          <!-- 收起态：一个小图标（有筛选时带圆点），点它展开侧边栏去切换 -->
          <button
            class="relative flex h-9 w-9 items-center justify-center rounded-lg text-base transition hover:bg-slate-800"
            :class="app.activeFilterTemplate ? 'text-sky-400' : 'text-slate-400'"
            :aria-label="
              app.activeFilterTemplate ? `当前只看「${app.activeFilterTemplate.name}」，点击展开侧边栏切换` : '设置筛选范围'
            "
            :title="
              app.activeFilterTemplate
                ? `当前只看「${app.activeFilterTemplate.name}」· 点此展开侧边栏切换`
                : '设置筛选范围（点此展开侧边栏）'
            "
            @click="openFilterPickerFromCollapsed()"
          >
            <FilterFunnelIcon class="h-4 w-4" />
            <span
              v-if="app.activeFilterTemplate"
              class="absolute right-1 top-1 h-2 w-2 rounded-full bg-sky-400 ring-2 ring-slate-900"
            ></span>
          </button>
        </template>

        <template v-if="!navExpanded">
          <div
            class="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white text-xs font-medium flex items-center justify-center select-none ring-2 ring-slate-700/60 hover:scale-105 transition"
            @mouseenter="showTip(`${auth.user?.username || ''} (${roleLabel(auth.user?.role)})`, $event)"
            @mouseleave="hideTip"
          >
            {{ (auth.user?.username || '?').slice(0, 1) }}
          </div>
          <button
            class="w-9 h-9 rounded-lg flex items-center justify-center text-base text-rose-400 hover:text-rose-300 hover:bg-slate-800 hover:scale-105 active:scale-95 transition"
            aria-label="退出登录"
            @click="auth.logout"
            @mouseenter="showTip('退出登录', $event)"
            @mouseleave="hideTip"
          >
            ⏻
          </button>
          <!-- 折叠态的测试通过按钮：同样要显眼（2026-10-11 用户反馈"不明显"）。
               未通过=琥珀实心底 + 白色✅，通过=绿底 —— 侧边栏收着一眼也能看到。 -->
          <button
            v-if="app.testPass.env === 'local'"
            class="w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0 transition
                   active:scale-95"
            :class="app.testPass.pass
              ? 'bg-emerald-500/25 text-emerald-300 ring-2 ring-emerald-400/50 hover:bg-emerald-500/35'
              : 'bg-amber-500 text-white ring-2 ring-amber-300/60 hover:bg-amber-400'"
            aria-label="标记本地测试通过"
            :disabled="app.testPass.loading"
            @click="app.markTestPass"
            @mouseenter="showTip(app.testPass.pass ? '✅ 本地测试已通过' : '⬜ 点我标记测试通过', $event)"
            @mouseleave="hideTip"
          >
            {{ app.testPass.loading ? '…' : '✅' }}
          </button>
        </template>
        <template v-else>
          <!-- 1.1.78: 展开态用户区重做。原来是一摞裸文字（灰"已登录" + 白姓名 + 红色"退出登录"），
               字号行距都随父级 text-xs，观感很毛、和折叠态那个渐变头像完全不像一套。
               现在收进一张卡片：渐变头像 + 在线点 + 姓名/角色 + 「已登录」小徽标，
               退出登录改成有 hover 底色的按钮（不再是裸红字），与角色权限页的卡片风格一致。 -->
          <div class="rounded-xl bg-slate-800/60 p-2.5 ring-1 ring-slate-700/60">
            <div class="flex items-center gap-2.5">
              <div class="relative shrink-0">
                <div
                  class="w-9 h-9 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white text-xs font-medium flex items-center justify-center select-none"
                >
                  {{ (auth.user?.username || '?').slice(0, 1) }}
                </div>
                <span
                  class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-slate-800"
                  aria-hidden="true"
                ></span>
              </div>
              <div class="min-w-0 flex-1">
                <!-- 姓名与「已登录」同一行；角色名另起一行给它整行宽度
                     （owner 的角色文案是「超级超级管理员」7 个字，和徽标挤一行会被截成"超级超级管…"） -->
                <div class="flex items-center gap-1.5">
                  <span class="min-w-0 truncate text-[13px] font-medium leading-tight text-white" :title="auth.user?.username || ''">
                    {{ auth.user?.username || '未登录' }}
                  </span>
                  <span
                    class="shrink-0 rounded bg-emerald-400/10 px-1.5 py-px text-[10px] font-medium text-emerald-300/90 ring-1 ring-emerald-400/20"
                    >已登录</span
                  >
                </div>
                <div class="mt-1 truncate text-[11px] text-slate-400" :title="roleLabel(auth.user?.role)">
                  {{ roleLabel(auth.user?.role) }}
                </div>
              </div>
            </div>
            <button
              class="mt-2.5 w-full rounded-lg bg-rose-500/10 px-2 py-1.5 text-[11px] font-medium text-rose-300 transition hover:bg-rose-500/20 hover:text-rose-200"
              @click="auth.logout"
            >
              退出登录
            </button>
            <!-- 本地测试门禁按钮（2026-10-11 改：原来收成 10px 灰字+小按钮，用户反馈"不明显"
                 点不到）。改成**整宽醒目块**：未通过=琥珀色实心+大字，通过=绿色描边。
                 这块只在本地环境出现（后端 env=local 才渲染，生产隐藏）。
                 放在「退出登录」下面独占一行 —— 不与任何操作混排，闭眼也能找到。 -->
            <div
              v-if="app.testPass.env === 'local'"
              class="mt-2.5"
            >
              <button
                type="button"
                :disabled="app.testPass.loading"
                class="w-full rounded-lg px-2.5 py-2 text-xs font-semibold transition
                       disabled:opacity-60 disabled:cursor-not-allowed"
                :class="app.testPass.pass
                  ? 'bg-emerald-500/15 text-emerald-300 ring-2 ring-emerald-400/40 hover:bg-emerald-500/25'
                  : 'bg-amber-500 text-slate-900 ring-2 ring-amber-300/60 hover:bg-amber-400 active:scale-[0.98]'"
                :title="app.testPass.msg || (app.testPass.pass
                  ? '本地测试已通过，可以 push；改动了要推送的代码后需重新点（merge 远端不影响）'
                  : '点一下记录「本地测试通过」，pre-push 门禁会核对这条标记')"
                @click="app.markTestPass"
              >
                <span class="mr-1">{{ app.testPass.pass ? '✅' : '⬜' }}</span>
                {{ app.testPass.loading ? '记录中…' : app.testPass.pass ? '本地测试已通过' : '点我标记测试通过' }}
              </button>
              <p
                class="mt-1 text-[10px] leading-tight"
                :class="app.testPass.pass ? 'text-emerald-400/80' : 'text-amber-300/70'"
              >
                {{ app.testPass.pass
                  ? '可以 push 了；改完代码需重新点'
                  : 'push 前门禁会拦，先点上面这个' }}
              </p>
            </div>
          </div>
        </template>
        <!-- 1.1.84: 开发模式开关 —— 只在本地环境（后端 env=local）渲染，与「本地测试通过」按钮同源隐藏。
             文案明写「开发模式 开 / 关」+ 指示灯圆点 + 底色反差；不放右下角常驻徽标。
             折叠态侧边栏宽度不足放不下文字按钮，故只在展开态出现（开发者通常展开侧边栏）。 -->
        <div v-if="dm.state.local && navExpanded" class="mt-2 flex items-center justify-between gap-2 border-t border-slate-700/50 pt-2">
          <button
            class="dev-code-btn"
            :class="[dm.state.enabled ? 'on' : '', dm.state.altHeld ? 'alt' : '']"
            :title="dm.state.enabled ? '开发模式已开启：按住 Alt 悬停任意元素可细分定位' : '开发模式已关闭（点击开启）'"
            @click="dm.toggle()"
          >
            <span class="dcb-dot"></span>{{ dm.state.enabled ? '开发模式 开' : '开发模式 关' }}
          </button>
          <span class="min-w-0 truncate text-[10px] text-slate-500">复制板块 code 给 AI</span>
        </div>
      </div>
    </aside>
    <main class="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
      <!-- 1.1.120: 本地开发「后端跑的是旧代码」红条 ✓
           触发条件：/api/version 返回 stale=true（后端源码修改时间 > 本进程启动时间）
           → 直接告诉用户重启哪条命令，不必再靠"数字没变"去猜 ✗ -->
      <div
        v-if="app.backendStale"
        class="backend-stale-banner sticky top-0 z-[136] flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm text-white"
        role="alert"
      >
        <span class="text-base leading-none">⚠</span>
        <span class="font-medium"
          >本地后端还在跑旧代码：进程启动 <b>{{ backendStartedLabel }}</b>，后端代码已改到
          <b>{{ backendSourceLabel }}</b></span
        >
        <code class="rounded bg-black/25 px-1.5 py-0.5 text-xs">bash start-local.sh --clean</code>
        <span class="text-xs opacity-85">（前端热更新是好的，所以看起来「界面变了、数字没变」）</span>
      </div>
      <!-- 1.1.73: 系统升级横幅 —— 检测到新版本就出现在**页面正上方**（sticky 在滚动容器顶部，
           滚动时也一直可见、不遮挡内容）；点击横幅任意处 = 立即刷新到新版；
           右侧 ✕ = 本次会话不再提示（下次打开页面自然就是新版）。
           放在 <main> 的第一个子元素是刻意的：不盖住页面顶部内容，也不受各页面自己的 fixed 元素影响。 -->
      <Transition name="update-toast">
        <div
          v-if="updateTipVisible"
          class="update-banner sticky top-0 z-[135] flex cursor-pointer select-none items-center gap-2.5 px-4 py-2.5 text-sm text-white"
          role="button"
          tabindex="0"
          :title="`点击刷新到新版本${updateTipLabel ? '（' + updateTipLabel + '）' : ''}`"
          @click="reloadForUpdate"
          @keydown.enter.prevent="reloadForUpdate"
          @keydown.space.prevent="reloadForUpdate"
        >
          <span class="text-base leading-none">🚀</span>
          <span class="font-medium"
            >系统已升级<span v-if="updateTipLabel"> 到 {{ updateTipLabel }}</span
            >，点击这里刷新到新版本</span
          >
          <span class="ml-auto flex items-center gap-2">
            <span class="rounded-lg bg-white/20 px-2.5 py-1 text-xs font-semibold ring-1 ring-white/30">立即刷新</span>
            <button
              class="rounded px-1.5 py-0.5 text-white/75 transition hover:bg-white/20 hover:text-white"
              title="本次会话不再提示（下次打开页面就是新版）"
              @click.stop="dismissUpdateTip"
            >
              ✕
            </button>
          </span>
        </div>
      </Transition>
      <div :class="['mx-auto', route.path === '/replenishment' ? 'p-3 max-w-none' : 'p-6 max-w-[1920px]']">
        <!-- 1.1.85: 数据范围受限横幅（常驻、无关闭按钮）。只在核心决策页显示，
             组件内部对"不受限"用户自动隐藏，不会打扰管理员。 -->
        <ScopeNotice v-if="scopeNoticeVisible" class="mb-3" />
        <!-- 板块切换加淡入淡出（原来直接硬切，很生硬）。
             ① 包一层 div 当动画载体：不少页面是**多根节点**（自己还挂着弹窗组件），
                Transition 只接受单个根元素，包一层最稳、页面内部结构不动；
             ② 只做 opacity、**不做位移** —— 页面里有 position:fixed 的元素
                （表格底部常驻横向滚动条 `table-hscroll-fixed`、备货页的 `replenish-hscroll-fixed`），
                祖先一旦有 transform 就会改它们的定位基准，滚动条会跑到屏幕左上角。 -->
        <RouterView v-slot="{ Component, route: pageRoute }">
          <Transition name="page-fade" mode="out-in">
            <div :key="pageRoute.path" class="page-fade-root">
              <component :is="Component" />
            </div>
          </Transition>
        </RouterView>
      </div>
    </main>
    <div
      v-if="showNetworkLoading"
      role="status"
      aria-live="polite"
      class="fixed left-[calc(50%+6.5rem)] top-4 z-[110] flex -translate-x-1/2 items-center gap-2 rounded-full border border-sky-200 bg-white/95 px-4 py-2 text-sm font-medium text-sky-700 shadow-lg backdrop-blur"
    >
      <span class="spinner" aria-hidden="true" />正在加载数据…
    </div>
    <div class="fixed top-4 right-4 z-[100] space-y-2">
      <button
        v-for="toast in app.toasts"
        :key="toast.id"
        class="toast px-4 py-2 rounded-lg shadow-lg text-sm text-white block"
        :class="toast.tone === 'success' ? 'bg-emerald-600' : toast.tone === 'error' ? 'bg-rose-600' : 'bg-slate-800'"
        @click="app.dismiss(toast.id)"
      >
        {{ toast.text }}
      </button>
    </div>
    <!-- 侧边栏瞬时提示（原生 title 有延迟，收起态图标必须"放上去就出来"） -->
    <Transition name="side-tip">
      <div
        v-if="tip.visible"
        class="side-tip fixed z-[120] whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium pointer-events-none"
        :style="{ left: `${tip.left}px`, top: `${tip.top}px` }"
      >
        {{ tip.text }}
      </div>
    </Transition>
    <!-- 1.1.73：旧的「右下角提示条」已移到 <main> 顶部（见上方 update-banner），此处不再保留 -->

    <!-- 1.1.78: 切换筛选的过渡遮罩 —— 切换要整页刷新，直接刷会"咔"一下很生硬；
         先用这层淡入的遮罩说明"正在重新取数"，观感上是主动作而不是闪屏。 -->
    <Transition name="filter-curtain">
      <div
        v-if="app.filterApplying"
        class="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/25 backdrop-blur-[2px]"
        role="status"
        aria-live="polite"
      >
        <div class="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-slate-700 shadow-xl">
          <span class="spinner" aria-hidden="true" />正在应用筛选，重新取数…
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* tooltip：配色写死在此（不依赖 Tailwind 任意透明度类，避免背景没生成导致白字看不见）；
   只做透明度淡入，避免与 translateY(-50%) 的定位 transform 互相覆盖 */
.side-tip {
  transform: translateY(-50%);
  background: #1e293b;
  color: #fff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  box-shadow: 0 10px 28px rgba(2, 6, 23, 0.42);
}
.side-tip-enter-active,
.side-tip-leave-active {
  transition: opacity 0.1s ease;
}
.side-tip-enter-from,
.side-tip-leave-to {
  opacity: 0;
}
/* 收起态：56px 窄条里原来的左侧竖条会贴边显得脏，改用背景高亮 + 图标放大表达选中 */
.sidebar-collapsed :deep(.nav-active)::before {
  display: none;
}
.sidebar-collapsed :deep(.nav-active) {
  background: rgba(56, 189, 248, 0.12);
  box-shadow: inset 0 0 0 1px rgba(56, 189, 248, 0.25);
}
/* ── 「收起侧边栏」把手（1.1.69 重做）──
   原来是一块贴在侧边栏**右边缘外侧**的小凸起（绝对定位 + right:-1 + 半圆角），
   用户反馈"凸出来"不好看。现在改成标题行右端的方形按钮：完全在侧边栏内部，
   不越界、不需要绝对定位（也就没有"跟宽度过渡不一致"的隐患），
   提示走项目自带的 .side-tip 浮层（无延迟、样式统一）而不是原生 title。 */
.sidebar-toggle {
  flex: none;
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(100, 116, 139, 0.4);
  border-radius: 8px;
  background: rgba(51, 65, 85, 0.45);
  color: #94a3b8;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.12s ease;
}
.sidebar-toggle:hover {
  border-color: rgba(125, 211, 252, 0.45);
  background: rgba(56, 189, 248, 0.16);
  color: #e0f2fe;
}
.sidebar-toggle:active {
  transform: scale(0.94);
}
.sidebar-toggle:focus-visible {
  outline: 2px solid rgba(125, 211, 252, 0.55);
  outline-offset: 2px;
}
/* 子菜单箭头：线性 chevron，展开时转 90° 指下（替代原来生硬的实心 ▶） */
.nav-chevron {
  margin-left: auto;
  flex: none;
  color: #64748b;
  transition:
    transform 0.18s ease,
    color 0.18s ease;
}
.nav-chevron.is-open {
  transform: rotate(90deg);
  color: #7dd3fc;
}
/* 板块（在库/在途/工厂/…）切换时的淡入淡出。
   ⚠️ 只用 opacity，**绝不能加 transform**：页面里有 position:fixed 的元素（表格底部常驻横向
   滚动条等），祖先一旦有 transform 就会改它们的定位基准 → 滚动条会跑错位置。 */
.page-fade-enter-active {
  transition: opacity 0.16s ease-out;
}
.page-fade-leave-active {
  transition: opacity 0.12s ease-in;
}
.page-fade-enter-from,
.page-fade-leave-to {
  opacity: 0;
}
/* ⚠️ 这层包装 div 不能用 `display: contents`：那样元素不生成盒子，**opacity 会完全失效**，
   动画就白做了。它是普通 block，套在页面外面不影响布局（父级也是普通 block）。 */
/* 1.1.73: 系统升级横幅（页面正上方）—— 蓝渐变 + 从顶部滑入。
   它是 <main> 里的 sticky 元素（不是 fixed），transform 不会影响内部的后代定位。 */
.update-banner {
  background-image: linear-gradient(90deg, #1d4ed8 0%, #2563eb 42%, #0ea5e9 100%);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}
/* 1.1.120: 本地后端旧进程告警条（红金渐变，够醒目但不刺眼） */
.backend-stale-banner {
  background-image: linear-gradient(90deg, #b91c1c 0%, #dc2626 46%, #f59e0b 100%);
  box-shadow: 0 6px 18px rgba(120, 20, 20, 0.22);
}
.update-banner:hover {
  filter: brightness(1.07);
}
.update-banner:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.85);
  outline-offset: -3px;
}
/* 1.1.78: 全局筛选的动效 —— 都只做透明度 + 轻微位移，不动布局；
   位移幅度很小（6px / 2px），避免在侧边栏底部显得跳动。 */
.filter-menu-enter-active,
.filter-menu-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}
/* 浮层从右侧弹出：从左侧 6px 处滑入（方向与展开方向一致） */
.filter-menu-enter-from,
.filter-menu-leave-to {
  opacity: 0;
  transform: translateX(-6px) scale(0.98);
}
/* 悬停预览：从被截断的那个名字"长"出来。
   元素带 origin-bottom-left（原点 = 卡片左下角 = 行内名字左端），
   淡入的同时由 0.35 放大到 1（并轻微上抬 4px），视觉上就是从缩写处延伸展开。 */
.filter-preview-enter-active,
.filter-preview-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.2s cubic-bezier(0.2, 0.9, 0.3, 1.1);
}
.filter-preview-enter-from,
.filter-preview-leave-to {
  opacity: 0;
  transform: scale(0.35) translateY(4px);
}
/* 切换筛选的过渡遮罩 */
.filter-curtain-enter-active,
.filter-curtain-leave-active {
  transition: opacity 0.18s ease;
}
.filter-curtain-enter-from,
.filter-curtain-leave-to {
  opacity: 0;
}
.update-toast-enter-active,
.update-toast-leave-active {
  transition:
    opacity 0.2s ease-out,
    transform 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}
.update-toast-enter-from,
.update-toast-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
